/* Loading a hand-made past into the value chart.
 *
 * The rules it works under are in `state/importValues.ts`. The two that matter
 * on screen: everything written this way is marked as typed in and drawn as
 * such, and nothing can land on or after the first day the book measured
 * itself — the recorded part is what actually happened, and an estimate laid
 * over it would replace a fact. Rows that fall there are reported, not dropped
 * quietly. */

import { useState } from 'react';
import { firstRecorded, readValues, toSnapshots, type ValueImport } from '../state/importValues';
import { useStore } from '../state/store';
import { money } from '../lib/format';
import { shortDate } from '../lib/format';

export function ImportValues({ say }: { say: (m: string) => void }) {
  const { state, putSnapshots } = useStore();
  const [text, setText] = useState('');
  const [read, setRead] = useState<ValueImport | null>(null);
  const [busy, setBusy] = useState(false);

  const cutoff = firstRecorded(state.data.snapshots);

  const parse = (raw: string) => {
    const result = readValues(raw, state.data.snapshots);
    if (result.points.length === 0) {
      setRead(null);
      if (result.afterRecord.length > 0) {
        return say(`Every row falls on or after ${shortDate(cutoff!)}, which the book recorded itself. Nothing there is overwritten.`);
      }
      return say(result.skipped.length
        ? `Nothing readable: ${result.skipped[0]!.why}.`
        : 'No dated values were found. Two columns are needed: date and value_usd.');
    }
    setRead(result);
  };

  const run = async () => {
    if (!read) return;
    setBusy(true);
    try {
      const written = await putSnapshots(toSnapshots(read.points));
      const parts = [`${written} day(s) filled in`];
      if (read.afterRecord.length) parts.push(`${read.afterRecord.length} left alone as already recorded`);
      say(`${parts.join(', ')}. They are marked as typed in, and the chart keeps them apart from what it measured.`);
      setRead(null);
      setText('');
    } catch (e) {
      say(`Could not write them: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  const first = read?.points[0];
  const last = read?.points[read.points.length - 1];

  return (
    <>
      <div className="field">
        <label htmlFor="value-paste">Paste a past, or choose a file</label>
        <textarea
          id="value-paste" className="textarea" data-mono="true" spellCheck={false}
          placeholder={'date,value_usd\n2025-01-31,4210.00\n2025-02-28,5130.00'}
          value={text}
          onChange={(e) => { setText(e.target.value); setRead(null); }}
        />
        <div className="hint">
          Two columns: <span className="mono">date</span> and{' '}
          <span className="mono">value_usd</span>. One row per day you know
          something about. A month apart is fine, the chart joins them.
          {cutoff ? <> Anything dated {shortDate(cutoff)} or later is refused: the book
          measured itself from then on, and that part is not an estimate.</> : null}
        </div>
      </div>

      <div className="control-row">
        <input
          type="file" accept=".csv,.tsv,.txt,.md" id="value-file" style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            void file.text().then((raw) => { setText(raw); parse(raw); });
          }}
        />
        <button className="btn btn-sm" onClick={() => document.getElementById('value-file')?.click()}>
          Choose a file
        </button>
        <button className="btn btn-sm btn-primary" disabled={text.trim() === ''}
                onClick={() => parse(text)}>
          Read it
        </button>
      </div>

      {read ? (
        <div className="card card-pad" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            {read.points.length} day(s), before anything is written
          </div>
          <div className="kv-list" style={{ marginBottom: 12 }}>
            <div className="kv">
              <span className="dim">From</span>
              <span className="num" style={{ marginLeft: 'auto' }}>
                {shortDate(first!.at)} · {money(first!.totalUsd)}
              </span>
            </div>
            <div className="kv">
              <span className="dim">To</span>
              <span className="num" style={{ marginLeft: 'auto' }}>
                {shortDate(last!.at)} · {money(last!.totalUsd)}
              </span>
            </div>
          </div>

          {read.afterRecord.length > 0 ? (
            <div className="hint" style={{ marginBottom: 10 }}>
              <strong>{read.afterRecord.length} row(s) will not be written.</strong> They fall on or
              after {shortDate(cutoff!)}, from when the book has been recording its own value. That
              part is a measurement and nothing here replaces it.
            </div>
          ) : null}

          {read.skipped.length > 0 ? (
            <div className="hint hint-warn" style={{ marginBottom: 10 }}>
              {read.skipped.length} row(s) could not be read:{' '}
              {read.skipped.slice(0, 5).map((s) => `line ${s.line}, ${s.why}`).join(' · ')}
            </div>
          ) : null}

          <div className="hint" style={{ marginBottom: 10 }}>
            These go in marked as typed in, listed below with a{' '}
            <span className="tag" data-kind="muted">TYPED IN</span> tag, and removable one by one.
            The money-paid-in line is not taken from here. It is worked out from your deposits for
            every date, so the two can never drift apart.
          </div>

          <div className="fab-row" style={{ marginTop: 6 }}>
            <button className="btn btn-block" disabled={busy} onClick={() => setRead(null)}>Leave it</button>
            <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void run()}>
              {busy ? 'Writing…' : `Fill in ${read.points.length}`}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
